<script setup lang="ts">
/**
 * La barra superiore: dove sono, e chi sono.
 *
 * Volutamente povera. La tentazione, in un pannello, è di riempirla —
 * scorciatoie, contatori, filtri globali — e il risultato è che il titolo della
 * pagina, che è l'unica informazione di cui si ha bisogno cento volte al
 * giorno, finisce schiacciato fra due file di icone. Qui ci sono il titolo, le
 * notifiche, il tema e l'utente. Nient'altro, e le tre icone hanno tutte
 * un'etichetta accessibile.
 *
 * È fissa allo scorrimento perché sotto ci sono tabelle da cinquecento righe:
 * senza, per tornare al menu bisognerebbe risalire. `scroll-padding-top` nelle
 * fondamenta tiene conto della sua altezza, così una riga cercata non finisce
 * sotto di essa.
 */

import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Menu from 'primevue/menu';
import { Bell, LogOut, Menu as MenuIcon, Moon, MonitorCog, PhoneCall, Sun, UserCog } from '@lucide/vue';
import { PERMISSIONS, ROLE_LABELS, type RoleCode } from '@bestehr/shared';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { api, indirizzoApi } from '@/lib/http';
import { iniziali } from '@/lib/testo';
import BesteLogo from '@/components/brand/BesteLogo.vue';
import GissiLabLogo from '@/components/brand/GissiLabLogo.vue';
import CollegaTalk from './CollegaTalk.vue';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

/** Chi lavora a Gissi vede GissiLab (vedi `AppSidebar`). */
const gissilab = computed(() => auth.user?.headquarter?.brand === 'gissilab');
const ui = useUiStore();

const titolo = computed(() => route.meta.titolo ?? 'BesteHR');

const ruolo = computed(() => {
  const codice = auth.user?.roleCode as RoleCode | undefined;
  return codice ? (ROLE_LABELS[codice] ?? codice) : '';
});

/**
 * Il proprio ritratto, quando c'è.
 *
 * Le iniziali restano disegnate **sotto** e la fotografia ci si posa sopra,
 * come nell'`Avatar` dell'app e per la stessa ragione: 309 persone su 505 non
 * hanno un ritratto, e non averlo non deve somigliare a un caricamento fermo o
 * a un guasto. Un cerchio con «MR» dentro risponde per intero alla domanda
 * «chi sono» — qui, per giunta, il nome sta anche scritto accanto.
 *
 * La lunghezza in byte nell'indirizzo: la fotografia nuova sta allo stesso
 * indirizzo della vecchia, e senza qualcosa che cambi il browser continua a
 * servire quella di prima — chi si è appena cambiato il ritratto dal profilo
 * concluderebbe che non ha funzionato. Stessa nota di `CampoFotografia`, senza
 * il contatore locale: qui non si carica niente, si guarda soltanto, e ogni
 * cambiamento passa comunque da un `auth.ricarica()` che rifà `sizeBytes`.
 */
const ritratto = computed(() => {
  const u = auth.user;
  if (!u?.avatar) return null;
  return indirizzoApi(`/users/${u.id}/immagine`, { v: u.avatar.sizeBytes });
});

// ------------------------------------------------------------------- tema

const iconaTema = computed(() =>
  ui.tema === 'light' ? Sun : ui.tema === 'dark' ? Moon : MonitorCog,
);

const etichettaTema = computed(() =>
  ui.tema === 'light'
    ? 'Tema chiaro — passa allo scuro'
    : ui.tema === 'dark'
      ? 'Tema scuro — segui il sistema'
      : 'Tema automatico — passa al chiaro',
);

// ------------------------------------------------------------- notifiche

/**
 * Il conteggio delle non lette.
 *
 * Si aggiorna quando la finestra torna in primo piano, non a intervalli fissi.
 * Un sondaggio ogni trenta secondi su cinquecento schede aperte è una richiesta
 * al secondo per un numerino; il momento in cui quel numero interessa davvero è
 * quello in cui si torna sul pannello dopo essere stati altrove.
 */
const nonLette = ref(0);

/**
 * ⚠ **Chi una bacheca non ce l'ha, la campana non la vede — e non la chiede.**
 *
 * Trovato il 2026-10-02 aprendo il Cruscotto con un'utenza **reception**: 403 su
 * `/notifications/unread-count` a **ogni pagina**, perché `notifications:read:own` non ce
 * l'hanno due ruoli su nove, `reception` e `magazzino_divise`. Non è una dimenticanza del
 * catalogo: `RECEPTION_EXTRA` non parte da `EMPLOYEE_BASE` di proposito — «il banco reception
 * non sta in `EMPLOYEE_BASE`, è un banco, non una persona» — ed è una decisione col suo perché
 * scritto. Se cambiarla è una domanda per Cosimo, non una riga da aggiungere di nascosto.
 *
 * Il difetto da questa parte è un altro, ed è tutto nostro: **il client chiedeva una rotta che
 * quel ruolo non raggiunge**, e disegnava un comando che porta a una pagina che risponde 403.
 * È §13 al rovescio — quel che si vede si può premere — ed è anche la famiglia di difetti che
 * `check-matrice-permessi` dichiara di **non** poter vedere, perché qui non c'è una voce di
 * menu da cui giudicare: c'è un'icona nella barra, uguale per tutti.
 *
 * Il permesso si chiede una volta e governa tutt'e due le cose, l'icona e la domanda: se un
 * giorno la decisione cambia, cambia il catalogo e qui non si tocca niente.
 */
const haBacheca = computed(() => auth.can(PERMISSIONS.NOTIFICATIONS_READ_OWN));

/** L'ultima lettura chiesta: due letture una dopo l'altra possono tornare a rovescio. */
let letturaNonLette = 0;

async function aggiornaNonLette(): Promise<void> {
  if (!auth.autenticato || !haBacheca.value) return;
  const mia = ++letturaNonLette;
  try {
    // `unread`, come lo dichiara `NotificationsService.unreadCount` e come lo legge l'app: qui si
    // leggeva `count`, che non arriva mai, e la campana restava a zero per sempre (campanaMuta).
    const res = await api.get<{ unread: number }>('/notifications/unread-count');
    // Aperte due comunicazioni di fila, il numero di prima (più alto) tornato dopo il secondo
    // rimetteva il pallino su una non letta che non c'è più.
    if (mia !== letturaNonLette) return;
    // Solo un numero vero sostituisce il numero di prima. Il `?? 0` di prima
    // trattava una risposta malformata come «nessuna da leggere»: il pallino
    // spariva, e sparire è precisamente ciò che un contatore di notifiche non
    // deve fare quando non sa. Come per l'errore di rete qui sotto, il valore
    // resta com'era e riproverà al prossimo ritorno sulla scheda.
    if (typeof res.unread === 'number' && Number.isFinite(res.unread)) nonLette.value = res.unread;
  } catch {
    // Un contatore che non si aggiorna non è un motivo per disturbare
    // l'utente: resta com'era e riproverà al prossimo ritorno sulla scheda.
  }
}

function suVisibilita(): void {
  if (document.visibilityState === 'visible') void aggiornaNonLette();
}

onMounted(() => {
  void aggiornaNonLette();
  document.addEventListener('visibilitychange', suVisibilita);
});

onBeforeUnmount(() => document.removeEventListener('visibilitychange', suVisibilita));

// Una lettura fatta in «Comunicazioni» cambia il numero adesso, non al prossimo cambio di scheda.
watch(() => ui.giroAvvisiLetti, () => void aggiornaNonLette());

// ---------------------------------------------------------------- utente

const menuUtente = useTemplateRef<InstanceType<typeof Menu>>('menuUtente');

/**
 * Se il menu è aperto, per dirlo al pulsante che lo apre.
 *
 * PrimeVue tiene lo stato dentro di sé e non lo espone in modo tipizzato, ma
 * emette `show` e `hide`: ascoltarli costa due righe e vale un `aria-expanded`
 * vero. Senza, il pulsante annuncia «comprimibile, compresso» per sempre — cioè
 * mente esattamente nel momento in cui il menu è spalancato sotto di esso, e chi
 * non vede lo schermo continua a premere per aprire una cosa già aperta,
 * chiudendola.
 */
const menuAperto = ref(false);

/*
  «Collega Beste Talk» (Cosimo, 2026-11-02: *«c'è anche il QR da scansionare sull'accesso cruscotto
  degli utenti normali abilitati al servizio per fare l'accesso?»*): solo a chi ha un interno attivo,
  letto una volta all'apertura (`GET /talk/collega`).
*/
const talkNumero = ref<string | null>(null);
const collegaAperta = ref(false);
onMounted(() => {
  void api
    .get<{ numero: string | null }>('/talk/collega')
    .then((r) => (talkNumero.value = r.numero))
    .catch(() => (talkNumero.value = null));
});

const vociUtente = computed(() => [
  {
    label: 'Il mio profilo',
    icona: UserCog,
    command: () => void router.push({ name: 'profilo' }),
  },
  // «Collega Beste Talk» resta anche qui, dove la si e' cercata la prima volta; quella in vista e'
  // il bottone della barra.
  ...(talkNumero.value
    ? [{ label: 'Collega Beste Talk', icona: PhoneCall, command: () => (collegaAperta.value = true) }]
    : []),
  {
    label: 'Esci',
    icona: LogOut,
    pericolo: true,
    command: async () => {
      await auth.logout();
      void router.push({ name: 'entra' });
    },
  },
]);
</script>

<template>
  <header class="topbar">
    <button
      type="button"
      class="icona solo-mobile"
      aria-label="Apri il menu"
      @click="ui.barraMobileAperta = true"
    >
      <MenuIcon :size="20" :stroke-width="1.75" />
    </button>

    <!--
      Il marchio accanto al menu, **solo su mobile**: lì la barra laterale è un cassetto
      chiuso, e senza questo nessuna pagina interna mostrerebbe il segno di Beste finché non
      si apre il menu. Su desktop la sidebar il logo ce l'ha già, quindi qui sparisce per non
      ripeterlo. `mark` e non `lockup`: in una barra stretta il logotipo si taglierebbe.
    -->
    <!-- GissiLab a 24 e non a 32 (`LEGGIMI-gissilab.md`): questa barra c'e' solo sui telefoni, dove 24 punti sono 48-72 pixel veri. -->
    <GissiLabLogo v-if="gissilab" :height="24" title="GissiLab" class="topbar__marchio solo-mobile" />
    <BesteLogo v-else variant="mark" :height="22" title="BesteHR" class="topbar__marchio solo-mobile" />

    <!--
      `h1` e non `div`: è il titolo della pagina, ed è ciò che un lettore di
      schermo annuncia quando si arriva. Cambia a ogni rotta perché lo prende
      dai metadati della rotta, che sono anche quelli che scrivono il titolo
      della scheda: un posto solo, due usi.
    -->
    <!-- `title` perché il titolo è troncato con i puntini quando la barra si
         stringe: senza, «Presenze e assenze del person…» non si può leggere per
         intero in nessun modo. Il suggerimento del sistema non aggiunge niente
         quando il testo ci sta tutto — mostra le stesse parole — e diventa
         l'unico modo di leggerlo quando non ci sta. -->
    <h1 class="titolo" :title="titolo">{{ titolo }}</h1>

    <div class="spazio" />

    <!--
      «Beste Talk» in vista (Cosimo, 2026-11-02: la voce nel menu del nome non la trovava, *«Mettilo
      in un posto piu logico»*): un bottone con la scritta, accanto alle notifiche, solo a chi ha un
      interno attivo. Apre il QR per collegare il telefono. Su un telefono resta solo l'icona.
    -->
    <button
      v-if="talkNumero"
      type="button"
      class="talk"
      :aria-label="`Collega Beste Talk — interno ${talkNumero}`"
      v-tooltip.bottom="'Collega Beste Talk al telefono'"
      @click="collegaAperta = true"
    >
      <PhoneCall :size="17" :stroke-width="1.85" aria-hidden="true" />
      <span class="talk__testo">Beste Talk</span>
    </button>

    <button
      type="button"
      class="icona"
      :aria-label="etichettaTema"
      v-tooltip.bottom="etichettaTema"
      @click="ui.ruotaTema()"
    >
      <Transition name="tema" mode="out-in">
        <component :is="iconaTema" :key="ui.tema" :size="19" :stroke-width="1.75" />
      </Transition>
    </button>

    <RouterLink
      v-if="haBacheca"
      :to="{ name: 'comunicazioni' }"
      class="icona"
      :aria-label="
        nonLette > 0 ? `Notifiche — ${nonLette} da leggere` : 'Notifiche — nessuna da leggere'
      "
      v-tooltip.bottom="'Notifiche'"
    >
      <Bell :size="19" :stroke-width="1.75" />
      <!--
        Il pallino non porta il numero quando il numero è grande: `99+` in un
        cerchio da sedici pixel non si legge, e la differenza fra centoventi e
        centoventidue non ha mai cambiato una decisione. `aria-hidden` perché il
        conteggio esatto sta già nell'etichetta del comando.
      -->
      <span v-if="nonLette > 0" class="pallino" aria-hidden="true">
        {{ nonLette > 99 ? '99+' : nonLette }}
      </span>
    </RouterLink>

    <!--
      Lo scopo del pulsante sta **dentro** il pulsante, non al posto suo.

      C'era un `aria-label="Menu del profilo"`, e un `aria-label` non si aggiunge
      al contenuto: lo sostituisce. Il nome e il ruolo restano scritti a schermo
      su desktop, ma per il sistema quel pulsante si chiamava «Menu del profilo»
      e nient'altro. Chi comanda a voce dice ciò che legge — «premi Mario Rossi»
      — e non succede niente: è il caso che WCAG 2.5.3 (*Label in Name*) descrive
      parola per parola, ed è anche l'unico modo in cui un pannello può risultare
      rotto a chi lo usa senza che nessuno degli altri se ne accorga mai.

      Il testo nascosto va **in coda** al testo visibile: così il nome
      accessibile comincia con quello che si legge («Mario Rossi Amministratore
      apri il menu del profilo»), che è la forma che i comandi vocali agganciano
      meglio. Sotto i 640 px il nome e il ruolo sono nascosti e resta il solo
      scopo, che lì è anche l'unica cosa vera: non c'è testo visibile da
      contraddire.
    -->
    <button
      type="button"
      class="utente"
      aria-haspopup="menu"
      :aria-expanded="menuAperto"
      @click="menuUtente?.toggle($event)"
    >
      <!--
        Tutto `aria-hidden`, ritratto compreso: il nome accessibile del pulsante
        è già il nome della persona, e una faccia con un `alt` accanto lo
        direbbe due volte. Vedi la nota sopra `ritratto` per il perché le
        lettere restano disegnate sotto invece di essere sostituite.
      -->
      <span class="avatar" aria-hidden="true">
        {{ iniziali(auth.user?.displayName ?? '') }}
        <img v-if="ritratto" :src="ritratto" alt="" class="avatar__foto" />
      </span>
      <span class="utente-testo">
        <span class="utente-nome">{{ auth.user?.displayName }}</span>
        <span class="utente-ruolo">{{ ruolo }}</span>
      </span>
      <span class="beste-sr-only">apri il menu del profilo</span>
    </button>

    <Menu
      ref="menuUtente"
      :model="vociUtente"
      :popup="true"
      @show="menuAperto = true"
      @hide="menuAperto = false"
    >
      <template #item="{ item, props }">
        <a v-bind="props.action" :class="{ 'voce-pericolo': item.pericolo }">
          <component :is="item.icona" :size="17" :stroke-width="1.75" />
          <span>{{ item.label }}</span>
        </a>
      </template>
    </Menu>
    <CollegaTalk v-model:visibile="collegaAperta" :numero="talkNumero" />
  </header>
</template>

<style scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: var(--beste-z-topbar);

  display: flex;
  align-items: center;
  gap: var(--beste-space-8);

  height: var(--beste-layout-topbar);
  flex: none;
  padding-inline: var(--beste-layout-gutterDesktop);

  /*
   * Fondale traslucido con sfocatura: sotto scorre il contenuto, e vederlo
   * scorrere *attraverso* la barra dice che la pagina si sta muovendo meglio di
   * qualunque ombra. `color-mix` sul token invece di un colore letterale, così
   * segue il tema senza una seconda regola.
   */
  background: color-mix(in srgb, var(--beste-surface-canvas) 82%, transparent);
  backdrop-filter: blur(12px) saturate(1.4);
  border-bottom: 1px solid var(--beste-border-subtle);
}

/* Senza il supporto alla sfocatura, il fondale deve tornare pieno: semitrasparente
   e basta significherebbe testo del contenuto che traspare dietro il titolo. */
@supports not (backdrop-filter: blur(1px)) {
  .topbar {
    background: var(--beste-surface-canvas);
  }
}

.titolo {
  font-size: var(--beste-font-h3-size);
  line-height: var(--beste-font-h3-line);
  font-weight: var(--beste-font-h3-weight);
  letter-spacing: var(--beste-font-h3-tracking);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spazio {
  flex: 1;
}

.icona {
  position: relative;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: var(--beste-radius-md);
  color: var(--beste-text-secondary);
  cursor: pointer;
  transition:
    background-color var(--beste-duration-fast) var(--beste-ease-standard),
    color var(--beste-duration-fast) var(--beste-ease-standard);
}

.icona:hover {
  background: var(--beste-surface-subtle);
  color: var(--beste-text-primary);
}

.solo-mobile {
  display: none;
}

.talk {
  display: inline-flex;
  align-items: center;
  gap: var(--beste-space-6);
  height: 34px;
  padding: 0 var(--beste-space-12);
  margin-right: var(--beste-space-4);
  border: 1px solid var(--beste-border-default);
  border-radius: 999px;
  background: var(--beste-surface-raised);
  color: var(--beste-text-primary);
  font: inherit;
  font-size: var(--beste-font-small-size);
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color var(--beste-duration-fast) var(--beste-ease-standard),
    border-color var(--beste-duration-fast) var(--beste-ease-standard);
}

.talk:hover {
  background: var(--beste-surface-subtle);
  border-color: var(--beste-accent-solid);
}

@media (max-width: 639px) {
  .talk {
    width: 36px;
    padding: 0;
    justify-content: center;
  }

  .talk__testo {
    display: none;
  }
}

/* Il marchio nella barra: prende il colore del testo (BesteLogo disegna con `currentColor`),
   e sta appena staccato dal pulsante del menu. */
.topbar__marchio {
  color: var(--beste-text-primary);
  place-items: center;
  margin-inline-end: var(--beste-space-4);
}

.pallino {
  position: absolute;
  top: 2px;
  inset-inline-end: 2px;

  min-width: 16px;
  height: 16px;
  padding-inline: 4px;
  border-radius: var(--beste-radius-full);

  background: var(--beste-status-danger-solid);
  color: var(--beste-text-onSolid);
  font-size: 10px;
  line-height: 16px;
  font-weight: var(--beste-font-bodyStrong-weight);
  text-align: center;

  /* Un anello del colore del fondale: stacca il pallino dalla campanella senza
     disegnare un bordo che in tema scuro sarebbe un alone chiaro. */
  box-shadow: 0 0 0 2px var(--beste-surface-canvas);
  animation: beste-rise-in var(--beste-duration-normal) var(--beste-ease-enter);
}

.tema-enter-active,
.tema-leave-active {
  transition:
    opacity var(--beste-duration-fast) var(--beste-ease-standard),
    transform var(--beste-duration-fast) var(--beste-ease-standard);
}

/* Il sole sorge, la luna tramonta: il verso della rotazione dice che il comando
   è un giro, non un interruttore. */
.tema-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.tema-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

.utente {
  display: flex;
  align-items: center;
  gap: var(--beste-space-8);
  height: 40px;
  padding-inline: var(--beste-space-6) var(--beste-space-12);
  margin-inline-start: var(--beste-space-4);
  border-radius: var(--beste-radius-full);
  cursor: pointer;
  transition: background-color var(--beste-duration-fast) var(--beste-ease-standard);
}

.utente:hover {
  background: var(--beste-surface-subtle);
}

.avatar {
  display: grid;
  place-items: center;
  position: relative;
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: var(--beste-radius-full);
  /* Senza, la fotografia sborderebbe dal cerchio e ne farebbe un quadrato. */
  overflow: hidden;
  background: var(--beste-accent-solid);
  color: var(--beste-text-onSolid);
  font-size: var(--beste-font-caption-size);
  font-weight: var(--beste-font-bodyStrong-weight);
  /* Le iniziali sono lettere, non numeri: le cifre tabulari attive globalmente
     qui non servono e allargano la spaziatura. */
  font-feature-settings: normal;
}

/*
  `cover` e non `contain`: al contrario della fotografia di un capannone, qui il
  riquadro è un quadrato deciso da noi e la faccia sta al centro di quello che le
  persone si scelgono. `contain` lascerebbe due bande d'accento ai lati di ogni
  ritratto orizzontale.
*/
.avatar__foto {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.utente-testo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  text-align: start;
}

.utente-nome {
  font-size: var(--beste-font-small-size);
  font-weight: var(--beste-font-bodyStrong-weight);
  color: var(--beste-text-primary);
}

.utente-ruolo {
  font-size: var(--beste-font-caption-size);
  color: var(--beste-text-muted);
}

.voce-pericolo {
  color: var(--beste-status-danger-text);
}

@media (max-width: 1023px) {
  .topbar {
    padding-inline: var(--beste-space-8) var(--beste-layout-gutterMobile);
  }

  .solo-mobile {
    display: grid;
  }
}

/* Sotto i 640 px restano l'avatar e le icone: nome e ruolo sono la prima cosa
   che si può togliere, perché chi guarda sa già chi è. */
@media (max-width: 639px) {
  .utente-testo {
    display: none;
  }

  .utente {
    padding-inline: var(--beste-space-4);
  }
}
</style>
